import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Tugmalar (redesign v2): pill shakl. primary -- brend sariq (sahifada bitta asosiy amal), secondary -- to'ldirilgan
 * neytral, ink -- qora (ro'yxat qatoridagi asosiy amal, sariq sahifada bittagina qoladi), outline -- oq/halqali,
 * ghost -- shaffof, danger -- qizil, link -- havola.
 */
type Variant = "primary" | "secondary" | "ink" | "outline" | "ghost" | "danger" | "link";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary: "bg-accent text-on-accent hover:bg-accent-hover border-transparent focus-visible:outline-ink",
  secondary: "bg-fill text-ink border-transparent hover:bg-fill-hover",
  ink: "bg-ink text-surface border-transparent hover:bg-ink/85",
  outline: "bg-surface text-ink border-border hover:bg-fill",
  ghost: "bg-transparent text-ink-soft border-transparent hover:bg-fill hover:text-ink",
  danger: "bg-danger-soft text-danger border-transparent hover:bg-danger hover:text-white",
  link: "bg-transparent text-primary border-transparent hover:underline px-0 h-auto",
};
const sizes: Record<Size, string> = {
  sm: "h-8 px-3 text-[12.5px]",
  md: "h-9 px-4 text-[13px]",
  lg: "h-11 px-5 text-[14px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "secondary", size = "md", loading, disabled, children, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-full border font-semibold whitespace-nowrap",
        "transition-[background-color,color,transform,box-shadow] duration-150 active:scale-[0.98] motion-reduce:active:scale-100",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100",
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {loading && <Loader2 size={14} className="animate-spin" />}
      {children}
    </button>
  );
});
